import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { BellIcon, SearchIcon, UserIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import MobileSidebar from './MobileSidebar';
import MerchantSupportChatbot from '../support/MerchantSupportChatbot';
interface DashboardLayoutProps {
  children: React.ReactNode;
}
const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children
}) => {
  const {
    isAuthenticated,
    logout,
    user
  } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace data-id="element-387" />;
  }
  // Format path to readable title
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard Overview';
    if (path.includes('/qrc/static-amount')) return 'Static QRC with Amount';
    if (path.includes('/qrc/static')) return 'Static QRC';
    if (path.includes('/qrc/dynamic')) return 'Dynamic Payment QRC';
    if (path.includes('/request-to-pay')) return 'Request to Pay';
    if (path.includes('/rtp-lists')) return 'RTP Lists';
    if (path.includes('/transaction-history')) return 'Transaction History';
    if (path.includes('/merchant-assessment')) return 'Merchant Assessment';
    if (path.includes('/disputes')) return 'Disputes';
    if (path.includes('/update-profile')) return 'Profile Settings';
    return 'Dashboard';
  };
  // Get initials for avatar
  const getInitials = (name: string) => {
    if (!name) return 'M';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="flex h-screen overflow-hidden bg-[#F8FAFC]" data-id="element-388">
      <Sidebar onLogout={logout} data-id="element-389" />
      <MobileSidebar onLogout={logout} data-id="element-390" />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-391">
        {/* Top Navbar - Glass Effect */}
        <header className="h-16 flex-shrink-0 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 z-20 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0" data-id="element-392">
          <div className="flex items-center" data-id="element-393">
            <h1 className="text-lg font-semibold text-slate-900 hidden sm:block" data-id="element-394">
              {getPageTitle()}
            </h1>
          </div>

          <div className="flex items-center space-x-4" data-id="element-395">
            <div className="relative hidden md:block" data-id="element-396">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-397">
                <SearchIcon className="h-4 w-4 text-slate-400" data-id="element-398" />
              </div>
              <input type="text" placeholder="Search transactions..." className="w-64 pl-10 pr-4 py-2 bg-slate-100/50 border border-transparent rounded-full text-sm focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all outline-none" data-id="element-399" />
            </div>

            <button className="relative p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors" data-id="element-400">
              <BellIcon className="h-5 w-5" data-id="element-401" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" data-id="element-402"></span>
            </button>

            <div className="h-8 w-px bg-slate-200 mx-1" data-id="element-403"></div>

            <div className="flex items-center space-x-3 cursor-pointer hover:bg-slate-50 p-1.5 rounded-lg transition-colors" data-id="element-404">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 border border-indigo-200 flex items-center justify-center text-indigo-700 font-semibold text-sm" data-id="element-405">
                {getInitials(user?.name || '')}
              </div>
              <div className="hidden md:block text-sm" data-id="element-406">
                <p className="font-medium text-slate-700 leading-none" data-id="element-407">
                  {user?.name || 'Merchant'}
                </p>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-408">Admin</p>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto custom-scrollbar" data-id="element-409">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-410">{children}</div>
        </main>
      </div>

      {/* AI Support Chatbot */}
      <MerchantSupportChatbot data-id="element-411" />
    </div>;
};
export default DashboardLayout;